var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable @typescript-eslint/no-empty-function */
import filesize from 'filesize';
import { v4 } from 'uuid';
import { useApolloClient } from '@apollo/client';
import { append } from 'libs/util/array';
import { createContext } from 'libs/react';
import { read } from 'libs/util/file';
import { remove } from 'libs/util/array';
import { useContext } from 'libs/react';
import { useState } from 'libs/react';
import { React } from 'libs/react';
import { CreateMediaMutation } from 'graphql/Media/mutations/CreateMediaMutation';
import { DeleteMediaMutation } from 'graphql/Media/mutations/DeleteMediaMutation';
/**
 * Must stay in sync with the `maxFileSize` passed to `graphqlUploadExpress`
 * on the backend (src/server.ts).
 *
 * @const MAX_MEDIA_FILE_SIZE
 * @since 1.0.0
 */
export const MAX_MEDIA_FILE_SIZE = 10 * 1000 * 1000;
/**
 * @component MediaProvider
 * @since 1.0.0
 */
export function MediaProvider(props) {
    const apollo = useApolloClient();
    const [uploads, setUploads] = useState([]);
    /**
     * @function createMedia
     * @since 1.0.0
     * @hidden
     */
    function createMedia(data) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const { file } = data;
            const id = v4();
            if (file.size > MAX_MEDIA_FILE_SIZE) {
                setUploads((uploads) => append(uploads.slice(), {
                    id,
                    data: '',
                    file,
                    filename: file.name,
                    filesize: file.size,
                    mimetype: file.type,
                    uploaded: 0,
                    progress: 0,
                    error: t('This file exceeds the maximum allowed size of {{size}}.').replace('{{size}}', filesize(MAX_MEDIA_FILE_SIZE))
                }));
                return undefined;
            }
            const upload = {
                id,
                file,
                data: yield read(file),
                filename: file.name,
                filesize: file.size,
                mimetype: file.type,
                uploaded: 0,
                progress: 0
            };
            setUploads((uploads) => append(uploads.slice(), upload));
            try {
                const result = yield apollo.mutate({
                    mutation: CreateMediaMutation,
                    variables: {
                        input: {
                            file
                        }
                    },
                    context: {
                        fetchOptions: {
                            onProgress(progress, uploaded) {
                                upload.progress = progress;
                                upload.uploaded = uploaded;
                                setUploads((uploads) => uploads.slice());
                            },
                            useUpload: true
                        }
                    },
                    // Refetch by operation name so every active MediasQuery updates,
                    // regardless of search / restricted variables used by MediaLibrary.
                    refetchQueries: ['MediasQuery'],
                    awaitRefetchQueries: true
                });
                setUploads((uploads) => remove(uploads.slice(), uploads.find((record) => record.id == upload.id)));
                return (_a = result.data) === null || _a === void 0 ? void 0 : _a.createMedia;
            }
            catch (error) {
                setUploads((uploads) => uploads.map((record) => record.id == upload.id
                    ? Object.assign(Object.assign({}, record), { error: getUploadErrorMessage(error) }) : record));
                return undefined;
            }
        });
    }
    /**
     * @function retryUpload
     * @since 1.0.0
     * @hidden
     */
    function retryUpload(id) {
        return __awaiter(this, void 0, void 0, function* () {
            const upload = uploads.find((record) => record.id == id);
            if (upload == null || upload.file == null) {
                return undefined;
            }
            setUploads((uploads) => remove(uploads.slice(), uploads.find((record) => record.id == id)));
            return createMedia({ file: upload.file });
        });
    }
    /**
     * @function dismissUpload
     * @since 1.0.0
     * @hidden
     */
    function dismissUpload(id) {
        setUploads((uploads) => remove(uploads.slice(), uploads.find((record) => record.id == id)));
    }
    /**
     * @function deleteMedia
     * @since 1.0.0
     * @hidden
     */
    function deleteMedia(data) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const { id } = data;
            const result = yield apollo.mutate({
                mutation: DeleteMediaMutation,
                variables: {
                    input: {
                        id
                    }
                },
                refetchQueries: ['MediasQuery'],
                awaitRefetchQueries: true
            });
            return (_a = result.data) === null || _a === void 0 ? void 0 : _a.deleteMedia;
        });
    }
    const context = {
        uploads,
        createMedia,
        deleteMedia,
        retryUpload,
        dismissUpload
    };
    return (React.createElement(MediaContext.Provider, { value: context }, props.children));
}
/**
 * @component MediaContext
 * @since 1.0.0
 */
export const MediaContext = createContext({
    uploads: [],
    createMedia: () => __awaiter(void 0, void 0, void 0, function* () { return undefined; }),
    deleteMedia: function () { },
    retryUpload: () => __awaiter(void 0, void 0, void 0, function* () { return undefined; }),
    dismissUpload: function () { }
});
/**
 * @component MediaContext
 * @since 1.0.0
 */
export function useMedia() {
    return useContext(MediaContext);
}
/**
 * @function getUploadErrorMessage
 * @since 1.0.0
 * @hidden
 */
function getUploadErrorMessage(error) {
    const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
    if (graphQLError) {
        return graphQLError.message;
    }
    return error.message.replace('GraphQL error:', '').trim();
}
